import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { makeStyles, Tooltip } from '@material-ui/core';

const useStyles = makeStyles({
  root: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

/**
 * One line of text that ends in an ellipsis when it does not fit, and shows
 * the full text in a tooltip on hover or keyboard focus, only while it is
 * cut off (short values get no redundant one). The tooltip stays open while
 * the pointer is on it, so its text can be read or selected. `revealed`
 * opens it from outside, for text that cannot take focus itself.
 */
export function TruncatedText({
  children,
  className,
  revealed = false,
}: {
  children: ReactNode;
  className?: string;
  revealed?: boolean;
}) {
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  // The full text while it is cut off, else empty. Measured on hover and
  // focus because the width changes with the window.
  const [fullText, setFullText] = useState('');
  const [open, setOpen] = useState(false);
  const measure = useCallback(() => {
    const el = ref.current;
    setFullText(
      el && el.scrollWidth > el.clientWidth ? el.textContent ?? '' : '',
    );
  }, []);
  useEffect(() => {
    if (revealed) {
      measure();
    }
  }, [revealed, measure]);

  return (
    <Tooltip
      title={fullText}
      open={(open || revealed) && fullText !== ''}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      placement="bottom-start"
      interactive
    >
      <div
        ref={ref}
        className={className ? `${classes.root} ${className}` : classes.root}
        onMouseEnter={measure}
        onFocus={measure}
      >
        {children}
      </div>
    </Tooltip>
  );
}
